// src/components/RansomwareView.js
// Ransomware Intelligence Dashboard — reads from local DB (synced nightly from ransomware.live)
// UPDATED: Added SEC 8-K tab, enriched group detail (TTPs/tools), updated stats

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  Shield, Search, Activity, Globe, Users, AlertTriangle,
  BarChart2, TrendingUp, Clock, ChevronRight, ExternalLink,
  RefreshCw, Target, Database, FileText, Hash, Wifi, Mail,
  Bitcoin, Code, X, Calendar, Building2, Flag, Zap,
  Lock, CheckCircle, XCircle, Settings, Briefcase, TrendingDown,
  AlertCircle, BookOpen
} from 'lucide-react';

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const countryFlag = (code) => {
  if (!code || code.length !== 2) return '';
  try { return String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65)); }
  catch { return ''; }
};

const fmt = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(d); }
};

const groupInitials = (name = '') =>
  name.split(/[\s\-_]+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');

// apiFetch now takes getToken (the Clerk function), not a pre-fetched token string.
// On any 401, it calls getToken({ skipCache: true }) for a fresh token and retries
// once. Simpler than parsing the response body — getToken({ skipCache: true }) is
// idempotent so this is safe even when the user is genuinely signed out (the retry
// will just 401 again and we surface that to the caller).
const apiFetch = async (path, getToken, options = {}) => {
  const doFetch = async (tok) => fetch(`/api/ransomware${path}`, {
    ...options,
    headers: { 'Authorization': `Bearer ${tok}`, 'Accept': 'application/json', ...options.headers }
  });

  let tok = await getToken();
  let res = await doFetch(tok);

  // Token likely expired mid-session — mint a fresh one and retry once
  if (res.status === 401) {
    console.log('[Ransomware] 401 received, refreshing token and retrying:', path);
    tok = await getToken({ skipCache: true });
    res = await doFetch(tok);
  }

  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error || `HTTP ${res.status}`);
  }
  return res.json();
};

const TABS = [
  { id: 'overview', label: 'Overview',        icon: Activity },
  { id: 'groups',   label: 'Threat Actors',   icon: Users },
  { id: 'victims',  label: 'Victims',          icon: Target },
  { id: 'news',     label: 'Cyber News',       icon: FileText },
  { id: 'filings',  label: 'SEC 8-K Filings',  icon: Briefcase },
  { id: 'stats',    label: 'Statistics',       icon: BarChart2 },
];

// ─── SHARED UI ────────────────────────────────────────────────────────────────
const Spinner = ({ label = 'Loading...' }) => (
  <div className="flex flex-col items-center justify-center py-20 gap-3">
    <RefreshCw className="w-7 h-7 text-red-400 animate-spin" />
    <span className="text-slate-500 text-sm">{label}</span>
  </div>
);

const Empty = ({ icon: Icon = Database, title, sub }) => (
  <div className="flex flex-col items-center justify-center py-20 gap-3 text-center px-8">
    <div className="w-14 h-14 rounded-2xl bg-slate-800/60 border border-slate-700/40 flex items-center justify-center">
      <Icon className="w-6 h-6 text-slate-500" />
    </div>
    {title && <div className="text-slate-400 font-medium">{title}</div>}
    {sub   && <div className="text-slate-600 text-sm max-w-xs">{sub}</div>}
  </div>
);

const MiniBar = ({ value, max, color = 'bg-cyan-500' }) => (
  <div className="h-1.5 bg-slate-800/60 rounded-full overflow-hidden w-full">
    <div className={`h-full ${color} rounded-full transition-all duration-500`}
      style={{ width: `${Math.min(100, (value / (max || 1)) * 100)}%` }} />
  </div>
);

const StatCard = ({ icon: Icon, label, value, sub, accent = 'cyan' }) => {
  const map = {
    cyan:    'from-cyan-500/10   border-cyan-500/20   text-cyan-400',
    red:     'from-red-500/10    border-red-500/20    text-red-400',
    orange:  'from-orange-500/10 border-orange-500/20 text-orange-400',
    purple:  'from-purple-500/10 border-purple-500/20 text-purple-400',
    emerald: 'from-emerald-500/10 border-emerald-500/20 text-emerald-400',
    blue:    'from-blue-500/10   border-blue-500/20   text-blue-400',
  };
  const cls = map[accent] || map.cyan;
  const tc = cls.split(' ').find(c => c.startsWith('text-'));
  return (
    <div className={`bg-gradient-to-br ${cls} to-transparent border rounded-xl p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${tc}`} />
        <span className="text-[11px] text-slate-500 font-medium uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-bold text-slate-100 tabular-nums">{value ?? '—'}</div>
      {sub && <div className="text-xs text-slate-600 mt-1">{sub}</div>}
    </div>
  );
};

const IocIcon = ({ type }) => {
  const t = (type || '').toLowerCase();
  if (t === 'ip')    return <Wifi    className="w-3.5 h-3.5 text-cyan-400" />;
  if (['md5','sha1','sha256','sha-256'].includes(t)) return <Hash className="w-3.5 h-3.5 text-purple-400" />;
  if (t === 'email') return <Mail    className="w-3.5 h-3.5 text-yellow-400" />;
  if (t === 'btc')   return <Bitcoin className="w-3.5 h-3.5 text-orange-400" />;
  if (t === 'domain')return <Globe   className="w-3.5 h-3.5 text-blue-400" />;
  if (t === 'url')   return <ExternalLink className="w-3.5 h-3.5 text-emerald-400" />;
  return <Code className="w-3.5 h-3.5 text-slate-400" />;
};

// ═════════════════════════════════════════════════════════════════════════════
// OVERVIEW TAB
// ═════════════════════════════════════════════════════════════════════════════
const OverviewTab = ({ stats, groups, victims }) => {
  const top8 = [...(groups||[])].sort((a,b)=>(b.victim_count||0)-(a.victim_count||0)).slice(0,8);
  const maxV = top8[0]?.victim_count || 1;

  const countryMap = {};
  (victims||[]).forEach(v => { if (v.country) countryMap[v.country] = (countryMap[v.country]||0)+1; });
  const topCountries = Object.entries(countryMap).sort((a,b)=>b[1]-a[1]).slice(0,6);

  const sectorMap = {};
  (victims||[]).forEach(v => { const s=v.sector||'Unknown'; sectorMap[s]=(sectorMap[s]||0)+1; });
  const topSectors = Object.entries(sectorMap).sort((a,b)=>b[1]-a[1]).slice(0,6);
  const maxS = topSectors[0]?.[1] || 1;

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Users}    label="Known Groups"   value={groups?.length ?? '—'}                              accent="red"    sub="ransomware groups" />
        <StatCard icon={Target}   label="Total Victims"  value={stats?.total_victims?.toLocaleString() ?? '—'}       accent="orange" sub="cumulative all-time" />
        <StatCard icon={Database} label="Victim Records" value={stats?.victim_records?.toLocaleString() ?? '—'}      accent="purple" sub="in local database" />
        <StatCard icon={FileText} label="News Reports"   value={stats?.news?.toLocaleString() ?? '—'}                accent="cyan"   sub="cyber attack articles" />
        <StatCard icon={Briefcase} label="SEC 8-K"       value={stats?.filings_8k?.toLocaleString() ?? '—'}          accent="blue"   sub="incident disclosures" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-slate-900/40 border border-slate-700/30 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-red-400" />Most Active Groups
          </h3>
          <div className="space-y-3">
            {top8.map((g,i) => (
              <div key={g.name} className="flex items-center gap-3">
                <span className="text-[10px] text-slate-600 w-4 text-right tabular-nums">{i+1}</span>
                <div className="w-7 h-7 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[9px] font-bold text-red-400">{groupInitials(g.name)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-medium text-slate-200 truncate">{g.name}</span>
                    <span className="text-xs text-slate-500 ml-2 tabular-nums">{(g.victim_count||0).toLocaleString()}</span>
                  </div>
                  <MiniBar value={g.victim_count||0} max={maxV}
                    color={i<3?'bg-red-500':i<6?'bg-orange-500':'bg-slate-500'} />
                </div>
              </div>
            ))}
            {top8.length===0 && <div className="text-slate-600 text-xs text-center py-4">No data — run sync first</div>}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-slate-900/40 border border-slate-700/30 rounded-xl p-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Flag className="w-3.5 h-3.5 text-orange-400" />Top Countries
            </h3>
            <div className="space-y-2">
              {topCountries.map(([c,n])=>(
                <div key={c} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5 text-slate-300"><span>{countryFlag(c)}</span>{c}</span>
                  <span className="text-slate-500 tabular-nums">{n}</span>
                </div>
              ))}
              {topCountries.length===0 && <div className="text-slate-600 text-xs">No data yet</div>}
            </div>
          </div>

          <div className="bg-slate-900/40 border border-slate-700/30 rounded-xl p-4">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Building2 className="w-3.5 h-3.5 text-purple-400" />Top Sectors
            </h3>
            <div className="space-y-2.5">
              {topSectors.map(([s,n])=>(
                <div key={s}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs text-slate-300 truncate">{s}</span>
                    <span className="text-xs text-slate-500 ml-2 tabular-nums">{n}</span>
                  </div>
                  <MiniBar value={n} max={maxS} color="bg-purple-500" />
                </div>
              ))}
              {topSectors.length===0 && <div className="text-slate-600 text-xs">No data yet</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-900/40 border border-slate-700/30 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4 text-cyan-400" />Latest Victims
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-700/40">
                {['Victim','Group','Country','Sector','Discovered'].map(h=>(
                  <th key={h} className="text-left py-2 pr-4 text-slate-500 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(victims||[]).slice(0,15).map((v,i)=>(
                <tr key={i} className="border-b border-slate-800/30 hover:bg-slate-800/20">
                  <td className="py-2 pr-4">
                    <div className="text-slate-200 font-medium truncate max-w-[160px]">{v.post_title||'—'}</div>
                    {v.website && <div className="text-slate-600 truncate max-w-[160px]">{v.website}</div>}
                  </td>
                  <td className="py-2 pr-4"><span className="px-1.5 py-0.5 bg-red-500/15 text-red-400 border border-red-500/20 rounded text-[10px]">{v.group_name||'—'}</span></td>
                  <td className="py-2 pr-4 text-slate-300">{v.country?`${countryFlag(v.country)} ${v.country}`:'—'}</td>
                  <td className="py-2 pr-4 text-slate-500 truncate max-w-[110px]">{v.sector||'—'}</td>
                  <td className="py-2 text-slate-600">{fmt(v.discovered_at)}</td>
                </tr>
              ))}
              {(!victims||victims.length===0)&&(
                <tr><td colSpan={5} className="py-8 text-center text-slate-600">No victim records — run sync first</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// GROUP DETAIL PANEL — now shows enriched TTPs (structured), tools, links
// ═════════════════════════════════════════════════════════════════════════════
const GroupDetailPanel = ({ group, getToken, onClose }) => {
  const [iocs,    setIocs]    = useState(null);
  const [victims, setVictims] = useState(null);
  const [section, setSection] = useState('overview');
  const [loadingIocs,    setLoadingIocs]    = useState(false);
  const [loadingVictims, setLoadingVictims] = useState(false);

  useEffect(() => {
    if (!group || !getToken) return;
    setIocs(null); setVictims(null); setSection('overview');

    setLoadingIocs(true);
    apiFetch(`/iocs/${encodeURIComponent(group.slug||group.name)}`, getToken)
      .then(setIocs).catch(()=>setIocs({total:0,iocs:{},types:{}}))
      .finally(()=>setLoadingIocs(false));

    setLoadingVictims(true);
    apiFetch(`/victims?group=${encodeURIComponent(group.name)}&limit=200`, getToken)
      .then(d=>setVictims(Array.isArray(d)?d:[]))
      .catch(()=>setVictims([]))
      .finally(()=>setLoadingVictims(false));
  }, [group, getToken]);

  if (!group) return null;

  const iocEntries = [];
  if (iocs?.iocs) Object.entries(iocs.iocs).forEach(([t,vals])=>(vals||[]).forEach(v=>iocEntries.push({type:t,value:v})));

  // Parse TTPs from DB — stored as "TA0001 Initial Access" strings
  const ttps = Array.isArray(group.ttps) ? group.ttps.filter(Boolean) : [];
  const tools = Array.isArray(group.tools) ? group.tools.filter(Boolean) : [];
  const links = Array.isArray(group.links) ? group.links.filter(Boolean) : [];

  // Try to parse raw_data for structured TTPs (from /groups/{name} detail)
  let structuredTtps = [];
  try {
    const raw = typeof group.raw_data === 'string' ? JSON.parse(group.raw_data) : group.raw_data;
    if (Array.isArray(raw?.ttps)) {
      structuredTtps = raw.ttps.filter(t => t.tactic_name);
    }
  } catch {}

  const subtabs = [
    { id:'overview', label:'Overview' },
    { id:'ttps',     label:`TTPs${ttps.length > 0 ? ` (${ttps.length})` : ''}` },
    { id:'iocs',     label:`IOCs${iocs?` (${iocs.total||0})`:''}`},
    { id:'victims',  label:`Victims${victims?` (${victims.length})`:''}`},
  ];

  return (
    <div className="h-full flex flex-col">
      <div className="px-5 py-4 border-b border-slate-700/40 bg-gradient-to-r from-red-950/20 to-transparent flex-shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-red-500/15 border border-red-500/25 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-red-400">{groupInitials(group.name)}</span>
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-100">{group.name}</h2>
              <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Target className="w-3 h-3 text-red-400" />{(group.victim_count||0).toLocaleString()} victims</span>
                {group.last_seen && <span><Clock className="w-3 h-3 inline mr-1" />{fmt(group.last_seen)}</span>}
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                  group.status==='active'?'bg-red-500/15 text-red-400 border-red-500/20':'bg-slate-700/50 text-slate-400 border-slate-600/30'
                }`}>{group.status||'active'}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-700/40 rounded-lg text-slate-500 hover:text-slate-200 transition-all"><X className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex border-b border-slate-700/40 flex-shrink-0 overflow-x-auto">
        {subtabs.map(t=>(
          <button key={t.id} onClick={()=>setSection(t.id)}
            className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-all ${
              section===t.id?'border-red-500 text-red-400':'border-transparent text-slate-500 hover:text-slate-300'
            }`}>{t.label}</button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {section==='overview' && (
          <div className="space-y-4">
            {group.description && (
              <div className="bg-slate-800/30 border border-slate-700/20 rounded-xl p-4">
                <p className="text-sm text-slate-300 leading-relaxed"
                   dangerouslySetInnerHTML={{ __html: group.description.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g,'') }} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {[['Victims',(group.victim_count||0).toLocaleString()],['First Seen',fmt(group.first_seen)],['Last Seen',fmt(group.last_seen)],['Status',group.status||'active']].map(([l,v])=>(
                <div key={l} className="bg-slate-800/30 border border-slate-700/20 rounded-xl p-3">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{l}</div>
                  <div className="text-sm font-semibold text-slate-200">{v||'—'}</div>
                </div>
              ))}
            </div>
            {tools.length > 0 && (
              <div className="bg-slate-800/30 border border-slate-700/20 rounded-xl p-4">
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Code className="w-3 h-3" />Known Tools
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {tools.map((t,i)=><span key={i} className="px-2 py-0.5 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded text-xs">{t}</span>)}
                </div>
              </div>
            )}
            {links.length > 0 && (
              <div className="bg-slate-800/30 border border-slate-700/20 rounded-xl p-4">
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">References</div>
                <div className="space-y-1.5">
                  {links.map((l,i)=>(
                    <a key={i} href={l} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 text-xs text-cyan-400 hover:text-cyan-300 truncate">
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />{l}
                    </a>
                  ))}
                </div>
              </div>
            )}
            {!group.description && tools.length === 0 && (
              <div className="text-slate-600 text-xs text-center py-8">
                Detail data populates after first sync with group enrichment.
              </div>
            )}
          </div>
        )}

        {section==='ttps' && (
          <div className="space-y-3">
            {structuredTtps.length > 0 ? (
              structuredTtps.map((tactic, i) => (
                <div key={i} className="bg-slate-800/30 border border-slate-700/20 rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 bg-orange-500/10 border-b border-orange-500/15 flex items-center gap-2">
                    <Shield className="w-3.5 h-3.5 text-orange-400" />
                    <span className="text-xs font-semibold text-orange-300">{tactic.tactic_name}</span>
                    {tactic.tactic_id && <span className="text-[10px] text-slate-500 ml-auto font-mono">{tactic.tactic_id}</span>}
                  </div>
                  {Array.isArray(tactic.techniques) && tactic.techniques.length > 0 && (
                    <div className="p-3 space-y-1.5">
                      {tactic.techniques.map((tech, j) => (
                        <div key={j} className="flex items-start gap-2 px-2 py-1.5 bg-slate-900/30 rounded-lg">
                          <ChevronRight className="w-3 h-3 text-slate-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="text-xs text-slate-300">{tech.technique_name || tech.name || String(tech)}</div>
                            {tech.technique_id && <div className="text-[10px] text-slate-600 font-mono">{tech.technique_id}</div>}
                            {tech.technique_details && (
                              <div className="text-[10px] text-slate-500 mt-0.5 leading-relaxed">{tech.technique_details}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : ttps.length > 0 ? (
              // Fallback: flat TTP strings
              <div className="flex flex-wrap gap-2">
                {ttps.map((t,i) => (
                  <span key={i} className="px-2.5 py-1 bg-orange-500/10 text-orange-400 border border-orange-500/20 rounded-lg text-xs">{t}</span>
                ))}
              </div>
            ) : (
              <Empty icon={Shield} title="No TTP Data" sub="TTPs populate after sync with group detail enrichment." />
            )}
          </div>
        )}

        {section==='iocs' && (
          loadingIocs ? <Spinner label="Loading IOCs..." /> : (
            <div>
              {iocs?.types && Object.keys(iocs.types).length>0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {Object.entries(iocs.types).map(([t,c])=>(
                    <div key={t} className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-800/50 border border-slate-700/30 rounded-lg text-xs">
                      <IocIcon type={t} /><span className="text-slate-300 capitalize">{t}</span><span className="text-slate-600">({c})</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-1">
                {iocEntries.slice(0,300).map((ioc,i)=>(
                  <div key={i} className="flex items-center gap-3 px-3 py-1.5 bg-slate-800/20 border border-slate-700/15 rounded-lg hover:bg-slate-800/40 group transition-colors">
                    <IocIcon type={ioc.type} />
                    <span className="text-[10px] capitalize text-slate-600 w-12 flex-shrink-0">{ioc.type}</span>
                    <span className="text-xs text-slate-300 font-mono truncate flex-1">{ioc.value}</span>
                    <button onClick={()=>navigator.clipboard?.writeText(ioc.value)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-700 rounded text-slate-500 transition-all">
                      <FileText className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {iocEntries.length===0 && <Empty icon={Database} title="No IOCs" sub="No indicators recorded for this group." />}
                {iocEntries.length>300 && <div className="text-xs text-slate-600 text-center py-2">Showing 300 of {iocEntries.length}</div>}
              </div>
            </div>
          )
        )}

        {section==='victims' && (
          loadingVictims ? <Spinner label="Loading victims..." /> : (
            <div className="space-y-2">
              {(victims||[]).map((v,i)=>(
                <div key={i} className="bg-slate-800/20 border border-slate-700/20 rounded-xl p-3 hover:bg-slate-800/40 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-200">{v.post_title||'—'}</div>
                      {v.website && <div className="text-xs text-cyan-400/80 truncate mt-0.5">{v.website}</div>}
                    </div>
                    {v.country && <span className="text-base flex-shrink-0">{countryFlag(v.country)}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {v.sector && <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded">{v.sector}</span>}
                    {v.discovered_at && <span className="text-[10px] text-slate-600">{fmt(v.discovered_at)}</span>}
                  </div>
                </div>
              ))}
              {(!victims||victims.length===0) && <Empty icon={Target} title="No victims recorded" sub="Victim data will populate after sync." />}
            </div>
          )
        )}
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// GROUPS TAB
// ═════════════════════════════════════════════════════════════════════════════
const GroupsTab = ({ groups, getToken }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const filtered = (groups||[]).filter(g=>!search||g.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="h-full flex">
      <div className="w-[320px] flex-shrink-0 border-r border-slate-700/40 flex flex-col">
        <div className="p-4 border-b border-slate-700/40">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" placeholder="Search groups..." value={search} onChange={e=>setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-800/50 border border-slate-700/40 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-red-500/40" />
          </div>
          <div className="text-xs text-slate-600 mt-2">{filtered.length} groups</div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.map(g=>(
            <button key={g.id} onClick={()=>setSelected(g)}
              className={`w-full text-left px-4 py-3 border-b border-slate-800/30 hover:bg-slate-800/30 border-l-2 transition-all ${
                selected?.id===g.id?'bg-slate-800/40 border-l-red-500':'border-l-transparent'
              }`}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[9px] font-bold text-red-400">{groupInitials(g.name)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-slate-200 truncate">{g.name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-slate-600">{(g.victim_count||0).toLocaleString()} victims</span>
                    {g.ttps?.length > 0 && <span className="text-[10px] text-orange-400/70">{g.ttps.length} TTPs</span>}
                  </div>
                </div>
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${g.status==='active'?'bg-red-500':'bg-slate-600'}`} />
              </div>
            </button>
          ))}
          {filtered.length===0 && <Empty icon={Users} title="No groups" sub="Groups load after first sync." />}
        </div>
      </div>
      <div className="flex-1 min-w-0 overflow-hidden">
        {selected
          ? <GroupDetailPanel group={selected} getToken={getToken} onClose={()=>setSelected(null)} />
          : <Empty icon={Users} title="Select a Group" sub="Click any threat actor to view full intelligence profile." />
        }
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// VICTIMS TAB
// ═════════════════════════════════════════════════════════════════════════════
const VictimsTab = ({ victims }) => {
  const [search,  setSearch]  = useState('');
  const [fcountry,setFcountry]= useState('');
  const [fgroup,  setFgroup]  = useState('');
  const [selected,setSelected]= useState(null);

  const countries = [...new Set((victims||[]).map(v=>v.country).filter(Boolean))].sort();
  const groups    = [...new Set((victims||[]).map(v=>v.group_name).filter(Boolean))].sort();

  const filtered = (victims||[]).filter(v=>{
    const q=search.toLowerCase();
    const matchQ=!q||(v.post_title||'').toLowerCase().includes(q)||(v.website||'').toLowerCase().includes(q);
    const matchC=!fcountry||v.country===fcountry;
    const matchG=!fgroup  ||v.group_name===fgroup;
    return matchQ&&matchC&&matchG;
  });

  return (
    <div className="h-full flex">
      <div className="w-[320px] flex-shrink-0 border-r border-slate-700/40 flex flex-col">
        <div className="p-4 border-b border-slate-700/40 space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" placeholder="Search victims..." value={search} onChange={e=>setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-800/50 border border-slate-700/40 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-orange-500/40" />
          </div>
          <div className="flex gap-2">
            <select value={fcountry} onChange={e=>setFcountry(e.target.value)}
              className="flex-1 px-2 py-1.5 bg-slate-800/50 border border-slate-700/40 rounded-lg text-xs text-slate-300">
              <option value="">All Countries</option>
              {countries.map(c=><option key={c} value={c}>{countryFlag(c)} {c}</option>)}
            </select>
            <select value={fgroup} onChange={e=>setFgroup(e.target.value)}
              className="flex-1 px-2 py-1.5 bg-slate-800/50 border border-slate-700/40 rounded-lg text-xs text-slate-300">
              <option value="">All Groups</option>
              {groups.map(g=><option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div className="text-xs text-slate-600">{filtered.length} records</div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.map((v,i)=>(
            <button key={i} onClick={()=>setSelected(v)}
              className={`w-full text-left px-4 py-3 border-b border-slate-800/30 hover:bg-slate-800/30 border-l-2 transition-all ${
                selected===v?'bg-slate-800/40 border-l-orange-500':'border-l-transparent'
              }`}>
              <div className="flex items-start gap-3">
                <Target className="w-4 h-4 text-orange-400/60 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-slate-200 truncate">{v.post_title||'—'}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] px-1.5 py-0.5 bg-red-500/15 text-red-400 rounded">{v.group_name||'—'}</span>
                    {v.country && <span className="text-[10px] text-slate-600">{countryFlag(v.country)} {v.country}</span>}
                  </div>
                </div>
              </div>
            </button>
          ))}
          {filtered.length===0 && <Empty icon={Target} title="No victims" sub="Victims load after first sync." />}
        </div>
      </div>
      <div className="flex-1 min-w-0 overflow-y-auto">
        {selected ? (
          <div className="p-6 space-y-5">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-100">{selected.post_title||'—'}</h2>
                {selected.website && (
                  <a href={`https://${selected.website}`} target="_blank" rel="noopener noreferrer"
                    className="text-sm text-cyan-400 hover:underline flex items-center gap-1 mt-1">
                    <ExternalLink className="w-3.5 h-3.5" />{selected.website}
                  </a>
                )}
              </div>
              <button onClick={()=>setSelected(null)} className="p-1.5 hover:bg-slate-700/40 rounded-lg text-slate-500"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[['Group',selected.group_name],['Country',selected.country?`${countryFlag(selected.country)} ${selected.country}`:null],['Sector',selected.sector],['Discovered',fmt(selected.discovered_at)],['Published',fmt(selected.published_at)],['Attacked',fmt(selected.attacked_at)]].map(([l,v])=>(
                <div key={l} className="bg-slate-800/30 border border-slate-700/20 rounded-xl p-3">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{l}</div>
                  <div className="text-sm font-semibold text-slate-200">{v||'—'}</div>
                </div>
              ))}
            </div>
            {selected.description && (
              <div className="bg-slate-800/30 border border-slate-700/20 rounded-xl p-4">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Description</div>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.description}</p>
              </div>
            )}
          </div>
        ) : (
          <Empty icon={Target} title="Select a Victim" sub="Click any record to view full attack details." />
        )}
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// NEWS TAB
// ═════════════════════════════════════════════════════════════════════════════
const NewsTab = ({ news }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const filtered = (news||[]).filter(n=>{
    const q=search.toLowerCase();
    return !q||(n.title||'').toLowerCase().includes(q)||(n.summary||'').toLowerCase().includes(q);
  });

  return (
    <div className="h-full flex">
      <div className="w-[360px] flex-shrink-0 border-r border-slate-700/40 flex flex-col">
        <div className="p-4 border-b border-slate-700/40">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" placeholder="Search news..." value={search} onChange={e=>setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-800/50 border border-slate-700/40 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-cyan-500/40" />
          </div>
          <div className="text-xs text-slate-600 mt-2">{filtered.length} articles</div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.map((n,i)=>(
            <button key={i} onClick={()=>setSelected(n)}
              className={`w-full text-left px-4 py-3 border-b border-slate-800/30 hover:bg-slate-800/30 border-l-2 transition-all ${
                selected===n?'bg-slate-800/40 border-l-cyan-500':'border-l-transparent'
              }`}>
              <div className="text-xs font-medium text-slate-200 line-clamp-2 leading-snug">{n.title||'—'}</div>
              <div className="flex items-center gap-2 mt-1.5">
                {n.country && <span className="text-[10px] text-slate-600">{countryFlag(n.country)}</span>}
                {n.ransomware_group && <span className="text-[10px] px-1.5 py-0.5 bg-red-500/15 text-red-400 rounded">{n.ransomware_group}</span>}
                {n.published_at && <span className="text-[10px] text-slate-600">{fmt(n.published_at)}</span>}
              </div>
            </button>
          ))}
          {filtered.length===0 && <Empty icon={FileText} title="No news" sub="News articles load after first sync." />}
        </div>
      </div>
      <div className="flex-1 min-w-0 overflow-y-auto">
        {selected ? (
          <div className="p-6 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-xl font-bold text-slate-100 leading-snug">{selected.title}</h2>
              <button onClick={()=>setSelected(null)} className="p-1.5 hover:bg-slate-700/40 rounded-lg text-slate-500 flex-shrink-0"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex flex-wrap gap-2">
              {selected.country && <span className="px-2.5 py-1 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-300">{countryFlag(selected.country)} {selected.country}</span>}
              {selected.ransomware_group && <span className="px-2.5 py-1 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400">{selected.ransomware_group}</span>}
              {selected.published_at && <span className="px-2.5 py-1 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-500">{fmt(selected.published_at)}</span>}
            </div>
            {selected.summary && (
              <div className="bg-slate-800/30 border border-slate-700/20 rounded-xl p-5">
                <p className="text-sm text-slate-300 leading-relaxed">{selected.summary}</p>
              </div>
            )}
            {selected.url && (
              <a href={selected.url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20 rounded-xl text-sm text-cyan-400 transition-all">
                <ExternalLink className="w-4 h-4" />Read Full Article
              </a>
            )}
          </div>
        ) : (
          <Empty icon={FileText} title="Select an Article" sub="Click any news item to read the full report." />
        )}
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// SEC 8-K FILINGS TAB — NEW
// ═════════════════════════════════════════════════════════════════════════════
const FilingsTab = ({ filings8k, loading8k }) => {
  const [search,   setSearch]   = useState('');
  const [fitem,    setFitem]    = useState('');
  const [selected, setSelected] = useState(null);

  const filtered = (filings8k||[]).filter(f => {
    const q = search.toLowerCase();
    const matchQ = !q ||
      (f.company_name||'').toLowerCase().includes(q) ||
      (f.ticker||'').toLowerCase().includes(q) ||
      (f.summary||'').toLowerCase().includes(q);
    const matchI = !fitem || f.item_type === fitem;
    return matchQ && matchI;
  });

  return (
    <div className="h-full flex">
      <div className="w-[360px] flex-shrink-0 border-r border-slate-700/40 flex flex-col">
        <div className="p-4 border-b border-slate-700/40 space-y-2">
          <div className="flex items-center gap-2 mb-1">
            <Briefcase className="w-4 h-4 text-blue-400" />
            <span className="text-xs font-semibold text-slate-300">SEC Cybersecurity Incident Filings</span>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" placeholder="Search company or ticker..." value={search} onChange={e=>setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-800/50 border border-slate-700/40 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500/40" />
          </div>
          <select value={fitem} onChange={e=>setFitem(e.target.value)}
            className="w-full px-3 py-1.5 bg-slate-800/50 border border-slate-700/40 rounded-lg text-xs text-slate-300">
            <option value="">All Items (1.05 + 8.01)</option>
            <option value="1.05">Item 1.05 — Material Cybersecurity Incidents</option>
            <option value="8.01">Item 8.01 — Other Events</option>
          </select>
          <div className="text-xs text-slate-600">{filtered.length} filings</div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading8k ? (
            <Spinner label="Loading 8-K filings..." />
          ) : (
            <>
              {filtered.map((f,i)=>(
                <button key={i} onClick={()=>setSelected(f)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-800/30 hover:bg-slate-800/30 border-l-2 transition-all ${
                    selected===f?'bg-slate-800/40 border-l-blue-500':'border-l-transparent'
                  }`}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[9px] font-bold text-blue-400">{f.ticker || 'SEC'}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-slate-200 truncate">{f.company_name||'—'}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {f.item_type && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                            f.item_type==='1.05'
                              ? 'bg-red-500/15 text-red-400 border-red-500/20'
                              : 'bg-blue-500/15 text-blue-400 border-blue-500/20'
                          }`}>Item {f.item_type}</span>
                        )}
                        {f.filed_at && <span className="text-[10px] text-slate-600">{fmt(f.filed_at)}</span>}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
              {filtered.length===0 && (
                <Empty icon={Briefcase} title="No 8-K filings"
                  sub={filings8k?.length===0 ? "SEC filings load after first sync." : "No filings match your filters."} />
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex-1 min-w-0 overflow-y-auto">
        {selected ? (
          <div className="p-6 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-blue-400">{selected.ticker || 'SEC'}</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-100">{selected.company_name||'—'}</h2>
                  {selected.ticker && <div className="text-sm text-slate-500 mt-0.5">NYSE/NASDAQ: {selected.ticker}</div>}
                </div>
              </div>
              <button onClick={()=>setSelected(null)} className="p-1.5 hover:bg-slate-700/40 rounded-lg text-slate-500 flex-shrink-0"><X className="w-4 h-4" /></button>
            </div>

            <div className="flex flex-wrap gap-2">
              {selected.item_type && (
                <span className={`px-3 py-1 rounded-lg text-xs font-medium border ${
                  selected.item_type==='1.05'
                    ? 'bg-red-500/10 border-red-500/20 text-red-400'
                    : 'bg-blue-500/10 border-blue-500/20 text-blue-400'
                }`}>
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  Item {selected.item_type} — {selected.item_type==='1.05' ? 'Material Cybersecurity Incident' : 'Other Event'}
                </span>
              )}
              {selected.filed_at && (
                <span className="px-3 py-1 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-400">
                  <Calendar className="w-3 h-3 inline mr-1" />Filed {fmt(selected.filed_at)}
                </span>
              )}
              {selected.cik && (
                <span className="px-3 py-1 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-500 font-mono">
                  CIK: {selected.cik}
                </span>
              )}
            </div>

            {selected.summary && (
              <div className="bg-slate-800/30 border border-slate-700/20 rounded-xl p-5">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <BookOpen className="w-3 h-3" />Filing Summary
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.summary}</p>
              </div>
            )}

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-semibold text-amber-300 mb-1">SEC Mandatory Disclosure</div>
                  <div className="text-xs text-slate-400 leading-relaxed">
                    {selected.item_type === '1.05'
                      ? 'This company filed an 8-K under Item 1.05, indicating a material cybersecurity incident that has had or is reasonably likely to have a material impact on the registrant.'
                      : 'This filing was submitted under Item 8.01 as a voluntary cybersecurity-related disclosure.'
                    }
                  </div>
                </div>
              </div>
            </div>

            {selected.filing_url && (
              <a href={selected.filing_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 rounded-xl text-sm text-blue-400 transition-all">
                <ExternalLink className="w-4 h-4" />View SEC Filing
              </a>
            )}
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center gap-4 p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
              <Briefcase className="w-7 h-7 text-blue-400" />
            </div>
            <div>
              <div className="text-slate-300 font-semibold mb-1">SEC 8-K Cybersecurity Filings</div>
              <div className="text-slate-600 text-sm max-w-xs">
                Publicly-traded companies are required to disclose material cybersecurity incidents to the SEC.
                Select a filing to view details.
              </div>
            </div>
            <div className="flex gap-3 mt-2">
              <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-center">
                <div className="text-xs font-semibold text-red-400">Item 1.05</div>
                <div className="text-[10px] text-slate-500 mt-0.5">Material Incidents</div>
              </div>
              <div className="px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg text-center">
                <div className="text-xs font-semibold text-blue-400">Item 8.01</div>
                <div className="text-[10px] text-slate-500 mt-0.5">Voluntary Disclosures</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// STATISTICS TAB
// ═════════════════════════════════════════════════════════════════════════════
const StatsTab = ({ stats, groups, victims }) => {
  const top15 = [...(groups||[])].sort((a,b)=>(b.victim_count||0)-(a.victim_count||0)).slice(0,15);
  const maxV = top15[0]?.victim_count || 1;

  const monthMap = {};
  (victims||[]).forEach(v=>{ if(!v.discovered_at) return; const m=v.discovered_at.slice(0,7); monthMap[m]=(monthMap[m]||0)+1; });
  const months = Object.entries(monthMap).sort((a,b)=>a[0].localeCompare(b[0])).slice(-12);
  const maxM = Math.max(...months.map(m=>m[1]),1);

  const sectorMap = {};
  (victims||[]).forEach(v=>{ const s=v.sector||'Unknown'; sectorMap[s]=(sectorMap[s]||0)+1; });
  const sectors = Object.entries(sectorMap).sort((a,b)=>b[1]-a[1]).slice(0,12);
  const maxS = sectors[0]?.[1] || 1;

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Users}     label="Groups in DB"    value={groups?.length??'—'}                              accent="red" />
        <StatCard icon={Target}    label="Victim Records"  value={(victims?.length??stats?.victim_records??0).toLocaleString()} accent="orange" />
        <StatCard icon={Database}  label="IOCs"            value={stats?.iocs?.toLocaleString()??'—'}                accent="purple" />
        <StatCard icon={FileText}  label="News Articles"   value={stats?.news?.toLocaleString()??'—'}                accent="cyan" />
        <StatCard icon={Briefcase} label="8-K Filings"     value={stats?.filings_8k?.toLocaleString()??'—'}          accent="blue" />
      </div>

      <div className="bg-slate-900/40 border border-slate-700/30 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-300 mb-5 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-cyan-400" />Monthly Victim Volume
        </h3>
        {months.length > 0 ? (
          <div className="flex items-end gap-1.5 h-28">
            {months.map(([month,count])=>(
              <div key={month} className="flex-1 flex flex-col items-center gap-1 group relative">
                <div className="absolute bottom-full mb-1 opacity-0 group-hover:opacity-100 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-[10px] text-slate-300 whitespace-nowrap z-10 pointer-events-none">
                  {count} · {month}
                </div>
                <div className="w-full bg-gradient-to-t from-cyan-600/70 to-cyan-400/50 rounded-t-sm min-h-[4px]" style={{height:`${(count/maxM)*100}%`}} />
                <span className="text-[8px] text-slate-700 truncate w-full text-center">{month.slice(5)}</span>
              </div>
            ))}
          </div>
        ) : <div className="text-slate-600 text-sm text-center py-8">No data — run sync first.</div>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-900/40 border border-slate-700/30 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2"><Users className="w-4 h-4 text-red-400" />Top 15 Groups</h3>
          <div className="space-y-2.5">
            {top15.map((g,i)=>(
              <div key={g.name} className="flex items-center gap-3">
                <span className="text-[10px] text-slate-600 w-5 text-right tabular-nums">{i+1}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs text-slate-300 truncate">{g.name}</span>
                    <span className="text-xs text-slate-500 ml-2 tabular-nums">{(g.victim_count||0).toLocaleString()}</span>
                  </div>
                  <MiniBar value={g.victim_count||0} max={maxV} color={i<3?'bg-red-500':i<7?'bg-orange-500':'bg-slate-600'} />
                </div>
              </div>
            ))}
            {top15.length===0 && <div className="text-slate-600 text-xs text-center py-4">No data yet</div>}
          </div>
        </div>

        <div className="bg-slate-900/40 border border-slate-700/30 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2"><Building2 className="w-4 h-4 text-purple-400" />Sector Targeting</h3>
          <div className="space-y-2.5">
            {sectors.map(([s,n],i)=>(
              <div key={s}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-xs text-slate-300 truncate">{s}</span>
                  <span className="text-xs text-slate-500 ml-2 tabular-nums">{n}</span>
                </div>
                <MiniBar value={n} max={maxS} color={i<3?'bg-purple-500':i<6?'bg-indigo-500':'bg-slate-600'} />
              </div>
            ))}
            {sectors.length===0 && <div className="text-slate-600 text-xs text-center py-4">No data yet</div>}
          </div>
        </div>
      </div>

      {stats?.last_sync && (
        <div className="bg-slate-900/40 border border-slate-700/30 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2"><Settings className="w-4 h-4 text-slate-400" />Last Sync Details</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[['Status',stats.last_sync.status],['Started',fmt(stats.last_sync.started_at)],['Completed',fmt(stats.last_sync.completed_at)],['Groups',stats.last_sync.groups_synced],['Victims',stats.last_sync.victims_synced],['IOCs',stats.last_sync.iocs_synced],['News',stats.last_sync.news_synced],['Notes',stats.last_sync.notes]].map(([l,v])=>(
              v != null ? (
                <div key={l} className="bg-slate-800/30 border border-slate-700/20 rounded-lg p-3">
                  <div className="text-[10px] text-slate-600 uppercase tracking-wider mb-1">{l}</div>
                  <div className={`text-sm font-semibold ${l==='Status'&&v==='success'?'text-emerald-400':l==='Status'&&v==='failed'?'text-red-400':'text-slate-300'}`}>{v??'—'}</div>
                </div>
              ) : null
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ═════════════════════════════════════════════════════════════════════════════
const RansomwareView = ({ isSuperAdmin }) => {
  const { getToken } = useAuth();
  const [tab,       setTab]       = useState('overview');
  const [stats,     setStats]     = useState(null);
  const [groups,    setGroups]    = useState([]);
  const [victims,   setVictims]   = useState([]);
  const [news,      setNews]      = useState([]);
  const [filings8k, setFilings8k] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [loading8k, setLoading8k] = useState(false);
  const [syncing,   setSyncing]   = useState(false);
  const [syncMsg,   setSyncMsg]   = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLastRefresh(new Date());
    try {
      const [s, g, v, n] = await Promise.all([
        apiFetch('/stats',                            getToken).catch(() => null),
        apiFetch('/groups?limit=500',                 getToken).catch(() => []),
        apiFetch('/victims?limit=500&order=discovered_at', getToken).catch(() => []),
        apiFetch('/news?limit=200',                   getToken).catch(() => []),
      ]);
      setStats(s);
      setGroups(Array.isArray(g) ? g : []);
      setVictims(Array.isArray(v) ? v : []);
      setNews(Array.isArray(n) ? n : []);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  // Load 8K filings separately (lazy — only when tab is opened or on full refresh)
  const load8k = useCallback(async () => {
    setLoading8k(true);
    try {
      const f = await apiFetch('/8k?limit=200', getToken).catch(() => []);
      setFilings8k(Array.isArray(f) ? f : []);
    } finally {
      setLoading8k(false);
    }
  }, [getToken]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Load 8K when that tab is first opened
  useEffect(() => {
    if (tab === 'filings' && filings8k.length === 0 && !loading8k) {
      load8k();
    }
  }, [tab]);

  const triggerSync = async () => {
    if (!isSuperAdmin || syncing) return;
    setSyncing(true); setSyncMsg(null);
    try {
      await apiFetch('/sync', getToken, { method: 'POST' });
      setSyncMsg({ ok: true, text: 'Sync started — data refreshes in ~3 min.' });
      setTimeout(() => { loadAll(); load8k(); }, 3 * 60 * 1000);
    } catch (e) {
      setSyncMsg({ ok: false, text: e.message });
    } finally {
      setSyncing(false);
    }
  };

  const noData = !loading && groups.length === 0 && victims.length === 0;

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-800/50 bg-slate-900/30 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-red-500/15 border border-red-500/25 flex items-center justify-center">
            <Lock className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h1 className="text-base font-bold text-slate-100 flex items-center gap-2">
              Ransomware Intelligence
              <span className="px-2 py-0.5 bg-red-500/20 border border-red-500/30 rounded-full text-[10px] text-red-400 font-semibold tracking-wider">LIVE</span>
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">Source: ransomware.live · Nightly sync · Loaded {lastRefresh.toLocaleTimeString()}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {syncMsg && (
            <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border ${
              syncMsg.ok ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
            }`}>
              {syncMsg.ok ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
              {syncMsg.text}
            </div>
          )}
          {noData && isSuperAdmin && !syncMsg && (
            <div className="text-xs text-amber-400 flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <AlertTriangle className="w-3.5 h-3.5" />No data — run first sync
            </div>
          )}
          <button onClick={loadAll} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/40 rounded-xl text-xs text-slate-400 hover:text-slate-200 transition-all disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh
          </button>
          {isSuperAdmin && (
            <button onClick={triggerSync} disabled={syncing}
              className="flex items-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-xl text-xs text-red-400 hover:text-red-300 transition-all disabled:opacity-50">
              <Zap className={`w-3.5 h-3.5 ${syncing ? 'animate-pulse' : ''}`} />
              {syncing ? 'Syncing…' : 'Run Sync'}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-800/50 bg-slate-900/20 flex-shrink-0 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                tab === t.id ? 'border-red-500 text-red-400 bg-red-500/5' : 'border-transparent text-slate-500 hover:text-slate-300 hover:bg-slate-800/20'
              }`}>
              <Icon className="w-4 h-4" />{t.label}
              {t.id === 'filings' && stats?.filings_8k > 0 && (
                <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded text-[10px] font-semibold">{stats.filings_8k}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {loading ? <Spinner label="Loading ransomware intelligence..." /> : (
          <>
            {tab === 'overview' && <OverviewTab stats={stats} groups={groups} victims={victims} />}
            {tab === 'groups'   && <GroupsTab   groups={groups} getToken={getToken} />}
            {tab === 'victims'  && <VictimsTab  victims={victims} />}
            {tab === 'news'     && <NewsTab     news={news} />}
            {tab === 'filings'  && <FilingsTab  filings8k={filings8k} loading8k={loading8k} />}
            {tab === 'stats'    && <StatsTab    stats={stats} groups={groups} victims={victims} />}
          </>
        )}
      </div>
    </div>
  );
};

export default RansomwareView;